import { PropsWithChildren, useEffect, useRef } from 'react';
import { PostHogProvider, usePostHog } from 'posthog-js/react';
import { Properties } from 'posthog-js';
import { usePlatform } from '@tofu/platform';
import { useAuth } from '@hotpot-arcade/fanforge-auth';
import { useFanforgeConfigs } from '@hotpot-arcade/fanforge-core-client';

// Inner component that has access to PostHog context
function AnalyticsProviderInner({ children }: PropsWithChildren) {
  const posthog = usePostHog();
  const jwtPayload = useAuth((s) => (s.state === 'authenticated' ? s.jwtPayload : null));
  const lastIdentifiedUserId = useRef<string | null>(null);

  // Automatically identify users when auth state changes
  useEffect(() => {
    if (jwtPayload && jwtPayload.sub !== lastIdentifiedUserId.current) {
      // Build properties based on auth method
      const properties: Properties = {
        userId: jwtPayload.sub,
        username: jwtPayload.username ?? 'N/A',
      };

      let userSnowflake = jwtPayload.sub;

      // Add discord-specific properties if applicable
      if (jwtPayload.amr === 'discord' && jwtPayload.discordId) {
        properties.discordId = jwtPayload.discordId;
        userSnowflake = jwtPayload.discordId;
      }

      posthog.identify(userSnowflake, properties);
      lastIdentifiedUserId.current = userSnowflake;
    }
  }, [jwtPayload, posthog]);

  return <>{children}</>;
}

export function AnalyticsProvider({ children }: PropsWithChildren): React.JSX.Element {
  const analyticsConfig = useFanforgeConfigs();
  const platform = usePlatform();
  const isDiscordActivity = platform.platform.platform === 'discord-activity';

  return (
    <PostHogProvider
      apiKey={analyticsConfig.postHogKey}
      options={{
        api_host: isDiscordActivity ? '/.proxy/posthog' : analyticsConfig.postHogHost,
        ui_host: isDiscordActivity ? analyticsConfig.postHogHost : undefined,
        autocapture: true,
        capture_pageview: 'history_change',
      }}
    >
      <AnalyticsProviderInner>{children}</AnalyticsProviderInner>
    </PostHogProvider>
  );
}
